/**
 * NEX Hub - Layout Component
 * Modern header and footer with gradient effects
 */

import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import LanguageSwitcher from '../common/LanguageSwitcher'
import { 
  Home, 
  Package, 
  BookOpen, 
  Github,
  Twitter,
  Linkedin,
  Mail,
  ExternalLink,
  Hexagon,
  Menu,
  X,
  User,
  LogOut,
  Settings,
  LayoutDashboard
} from 'lucide-react'

export default function HubLayout({ children }) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  
  const isActive = (path) => {
    return location.pathname === path
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const navLinks = [
    { name: t('nav.home'), path: '/hub', icon: Home },
    { name: t('nav.browse'), path: '/hub/browse', icon: Package },
    { name: t('nav.docs'), path: '/hub/docs', icon: BookOpen },
  ]

  const closeMobileMenu = () => setMobileMenuOpen(false)

  return (
    <div className="min-h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur-md border-b border-gray-800 shadow-2xl">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link 
              to="/hub" 
              className="flex items-center gap-3 group"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <div className="relative bg-gradient-to-br from-blue-600 to-purple-600 p-2 rounded-xl shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                  <Hexagon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <div className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  NEX Hub
                </div>
                <div className="text-xs text-gray-400 -mt-1">Agent Marketplace</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              {navLinks.map(link => {
                const Icon = link.icon
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-200 ${
                      isActive(link.path)
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {link.name}
                  </Link>
                )
              })}
              <a
                href="https://github.com/INOSX/nex-agents"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-all duration-200"
              >
                <Github className="w-4 h-4" />
                GitHub
              </a>

              {/* Language Switcher (Desktop) */}
              <LanguageSwitcher />

              {/* User Menu (Desktop) */}
              <div className="relative ml-4">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-3 px-4 py-2 rounded-xl bg-gray-800 hover:bg-gray-700 transition-all duration-200 border border-gray-700"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm text-gray-300 max-w-[150px] truncate">
                    {user?.email}
                  </span>
                </button>

                {/* User Dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl overflow-hidden z-50">
                    <div className="p-4 border-b border-gray-700">
                      <p className="text-sm font-semibold text-white mb-1">{t('userMenu.signedInAs')}</p>
                      <p className="text-sm text-gray-400 truncate">{user?.email}</p>
                    </div>
                    <div className="py-2">
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        <span>{t('userMenu.dashboard')}</span>
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        <span>{t('userMenu.settings')}</span>
                      </Link>
                    </div>
                    <div className="border-t border-gray-700">
                      <button
                        onClick={() => {
                          setUserMenuOpen(false)
                          handleSignOut()
                        }}
                        className="flex items-center gap-3 px-4 py-3 w-full text-left text-red-400 hover:bg-gray-700 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{t('userMenu.signOut')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}}
            </button>
          </nav>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-800 bg-gray-900/95 backdrop-blur-md">
              <div className="flex flex-col gap-2">
                {navLinks.map(link => {
                  const Icon = link.icon
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                        isActive(link.path)
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {link.name}
                    </Link>
                  )
                })}
                <a
                  href="https://github.com/INOSX/nex-agents"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-all duration-200"
                >
                  <Github className="w-5 h-5" />
                  GitHub
                </a>

                {/* User Menu (Mobile) */}
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-center gap-3 px-4 py-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{user?.email}</p>
                      <p className="text-xs text-gray-400">Signed in</p>
                    </div>
                  </div>
                  <Link
                    to="/dashboard"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-all duration-200"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    {t('userMenu.dashboard')}
                  </Link>
                  <Link
                    to="/settings"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-all duration-200"
                  >
                    <Settings className="w-5 h-5" />
                    {t('userMenu.settings')}
                  </Link>
                  <button
                    onClick={() => {
                      closeMobileMenu()
                      handleSignOut()
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-red-400 hover:bg-gray-800 transition-all duration-200 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    {t('userMenu.signOut')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-gradient-to-br from-blue-600 to-purple-600 p-2 rounded-xl">
                  <Hexagon className="w-5 h-5 text-white" />
                </div>
                <div className="text-xl font-bold text-white">NEX Hub</div>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                The universal marketplace for AI agents. Discover, install, and share powerful agents for NEX Framework.
              </p>
              <div className="flex items-center gap-3">
                <a 
                  href="#" 
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5 text-gray-400 hover:text-blue-400 transition-colors" />
                </a>
                <a 
                  href="https://github.com/INOSX/nex-agents" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="GitHub"
                >
                  <Github className="w-5 h-5 text-gray-400 hover:text-white transition-colors" />
                </a>
                <a 
                  href="#" 
                  className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5 text-gray-400 hover:text-blue-500 transition-colors" />
                </a>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/hub/browse" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    Browse Agents
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
                <li>
                  <Link to="/hub/browse?type=nex" className="text-gray-400 hover:text-white transition-colors text-sm">
                    NEX Agents
                  </Link>
                </li>
                <li>
                  <Link to="/hub/browse?type=bmad" className="text-gray-400 hover:text-white transition-colors text-sm">
                    BMAD Agents
                  </Link>
                </li>
                <li>
                  <Link to="/hub/install" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Installation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/hub/docs" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Documentation
                  </Link>
                </li>
                <li>
                  <Link to="/hub/docs/quick-start" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Quick Start
                  </Link>
                </li>
                <li>
                  <Link to="/hub/docs/user-guide" className="text-gray-400 hover:text-white transition-colors text-sm">
                    User Guide
                  </Link>
                </li>
                <li>
                  <Link to="/hub/install" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Installation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-3">
                <li>
                  <a href="https://inosx.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    About INOSX
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
                <li>
                  <a href="https://github.com/INOSX/nex-agents" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm">
                    GitHub
                  </a>
                </li>
                <li>
                  <a href="mailto:contact@inosx.com" className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2">
                    <Mail className="w-3 h-3" />
                    Contact
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-gray-400 text-sm">
                © 2026 INOSX. All rights reserved.
              </div>
              <div className="flex items-center gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Terms of Service
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors text-sm">
                  License
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
